import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InternationalPhoneInput, isValidPhoneNumber } from '@/components/ui/phone-input';
import { useLeadSubmit, LeadSource } from '@/hooks/useLeadSubmit';
import { useLeadGate } from '@/contexts/LeadGateContext';
import { useBrochureDownload } from '@/hooks/useBrochureDownload';
import { useFloorPlanDownload } from '@/hooks/useFloorPlanDownload';
import { cn } from '@/lib/utils';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Lead source for analytics/CRM */
  source: LeadSource;
  /** Optional custom title */
  title?: string;
  /** Optional custom subtitle */
  subtitle?: string;
  /** Optional custom CTA text */
  ctaLabel?: string;
  /** Callback on successful registration */
  onSuccess?: () => void;
  /** Optional download URL for floor plans */
  downloadUrl?: string;
  /** Optional download name for floor plans */
  downloadName?: string;
}

const sourceConfig: Record<string, { title: string; subtitle: string; cta: string }> = {
  brochure: {
    title: 'Download Project Brochure',
    subtitle: 'Get the complete project overview with specifications and layouts.',
    cta: 'Download Brochure',
  },
  price: {
    title: 'View Detailed Pricing',
    subtitle: 'Access configuration-wise pricing and current offers.',
    cta: 'View Pricing',
  },
  floorplan: {
    title: 'View Floor Plans',
    subtitle: 'Access detailed layouts and configuration options.',
    cta: 'View Floor Plans',
  },
  floor_plan: {
    title: 'View Floor Plans',
    subtitle: 'Access detailed layouts and configuration options.',
    cta: 'View Floor Plans',
  },
  site_visit: {
    title: 'Book Site Visit',
    subtitle: 'Schedule a visit to experience Nahar Chandi Valley.',
    cta: 'Book Site Visit',
  },
  virtual_tour: {
    title: 'Watch Virtual Tour',
    subtitle: 'Experience an immersive 360° walkthrough of the project.',
    cta: 'Watch Virtual Tour',
  },
  callback: {
    title: 'Request Call Back',
    subtitle: 'Our advisor will call you to discuss your requirements.',
    cta: 'Request Call Back',
  },
  contact: {
    title: 'Get Project Details',
    subtitle: 'Receive complete project information from our team.',
    cta: 'Get Details',
  },
  dossier: {
    title: 'Download Project Dossier',
    subtitle: 'Get the comprehensive investment dossier.',
    cta: 'Download Dossier',
  },
  exit_intent: {
    title: 'Before You Go',
    subtitle: 'Register to receive project updates and exclusive offers.',
    cta: 'Register Now',
  },
  whatsapp_widget: {
    title: 'Get Project Details',
    subtitle: 'Receive complete project information from our team.',
    cta: 'Get Details',
  },
};

// Email validation removed - email is no longer collected

/**
 * RegistrationModal - Single unified registration form for all gated actions
 * 
 * Collects: Name + Phone + Email (all mandatory)
 * Grants: 24-hour full site access
 */
const RegistrationModal = ({
  isOpen,
  onClose,
  source,
  title,
  subtitle,
  ctaLabel,
  onSuccess,
  downloadUrl,
  downloadName,
}: RegistrationModalProps) => {
  const { isUnlocked, grantAccess } = useLeadGate();
  const { downloadBrochure } = useBrochureDownload();
  const { downloadMasterLayout } = useFloorPlanDownload();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const config = sourceConfig[source] || sourceConfig.contact;
  const displayTitle = title || config.title;
  const displaySubtitle = subtitle || config.subtitle;
  const displayCta = ctaLabel || config.cta;

  // Download helper
  const downloadFromUrl = useCallback((url: string, label?: string) => {
    const safeLabel = (label || 'floor-plan').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const link = document.createElement('a');
    link.href = url;
    link.download = `nahar-chandi-valley-${safeLabel}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Handle downloads based on source
  const triggerDownload = useCallback(() => {
    if (source === 'brochure' || source === 'dossier') {
      downloadBrochure();
    } else if (source === 'floorplan' || source === 'floor_plan') {
      if (downloadUrl) {
        downloadFromUrl(downloadUrl, downloadName);
      } else {
        downloadMasterLayout();
      }
    }
  }, [source, downloadUrl, downloadName, downloadBrochure, downloadMasterLayout, downloadFromUrl]);

  // IMPORTANT: All hooks must be called before any conditional returns (Rules of Hooks)
  const { submitLead, isSubmitting, isSuccess, reset } = useLeadSubmit({
    source,
    onSuccess: () => {
      // Grant 24h access
      grantAccess({
        name: formData.name,
        phone: formData.phone,
      });
      
      // Trigger any downloads
      triggerDownload();
      
      onSuccess?.();
      
      // Close after showing success
      setTimeout(() => {
        reset();
        onClose();
      }, 3000);
    },
  });

  // Track if auto-deliver has been triggered to prevent double-fires in Strict Mode
  const hasAutoDelivered = useRef(false);

  // Capture the unlock state at the moment the modal opens. Only close
  // defensively when the modal was opened for an ALREADY-unlocked user —
  // never when the unlock results from a successful submit, which must keep
  // the modal open long enough to show the Thank You confirmation
  const unlockedAtOpen = useRef(false);

  useEffect(() => {
    if (isOpen) {
      unlockedAtOpen.current = isUnlocked;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Safe auto-close for already unlocked users (defensive: useGateAction should prevent this)
  // Uses useEffect to avoid side effects during render and safely closes the modal
  useEffect(() => {
    if (isOpen && isUnlocked && unlockedAtOpen.current && !hasAutoDelivered.current) {
      hasAutoDelivered.current = true;
      // Close the modal immediately - useGateAction should have already handled the action
      onClose();
    }
    // Reset ref when modal closes
    if (!isOpen) {
      hasAutoDelivered.current = false;
    }
  }, [isOpen, isUnlocked, onClose]);

  // ALL useCallback hooks must be declared before any conditional returns
  const validateForm = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = 'Full name is required';
    } else if (trimmedName.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (/\d/.test(trimmedName)) {
      newErrors.name = 'Name should not contain numbers';
    }

    if (!formData.phone) {
      newErrors.phone = 'Mobile number is required';
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid mobile number';
    }

    if (formData.honeypot) {
      newErrors.honeypot = 'Spam detected';
    }

    setErrors(newErrors);
    return newErrors;
  }, [formData]);

  // NOTE: No early return based on isUnlocked - useGateAction is the single source of truth
  // If modal opens while unlocked (shouldn't happen), useEffect above will close it safely

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    await submitLead({
      name: formData.name,
      phone: formData.phone,
      honeypot: formData.honeypot,
      contactChannel: 'phone',
    });
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const isFormValid = 
    formData.name.trim().length >= 2 && 
    isValidPhoneNumber(formData.phone);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-card max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-foreground">
            {displayTitle}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">{displaySubtitle}</p>
        </DialogHeader>

        {/* Success State */}
        {isSuccess ? (
          <div className="flex flex-col items-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
            <h3 className="font-sans text-xl font-semibold text-foreground mb-2">Thank You!</h3>
            <p className="font-sans text-muted-foreground text-center">
              You now have full access to all project details. Our team will contact you shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            {/* Honeypot */}
            <input
              type="text"
              name="website"
              value={formData.honeypot}
              onChange={(e) => handleChange('honeypot', e.target.value)}
              className="absolute -left-[9999px] opacity-0"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Full Name <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                placeholder="Your Full Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={cn(errors.name && 'border-destructive')}
              />
              {errors.name && (
                <p className="text-xs text-destructive mt-1">{errors.name}</p>
              )}
            </div>


            {/* Phone Field */}
            <InternationalPhoneInput
              value={formData.phone}
              onChange={(value) => handleChange('phone', value)}
              error={!!errors.phone}
              placeholder="Enter Mobile Number"
              label="Mobile Number"
              required
            />
            {errors.phone && (
              <p className="text-xs text-destructive -mt-2">{errors.phone}</p>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="w-full h-12 text-base font-semibold gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  {displayCta}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>

            {/* Compliance Text */}
            <p className="text-xs text-muted-foreground text-center pt-1">
              By submitting, you agree to be contacted about Nahar Chandi Valley.{' '}
              <Link to="/privacy-policy" className="text-accent hover:underline">
                Privacy protected
              </Link>.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationModal;
